import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { SharpenService } from '@services/SharpenService/sharpen.service';
import { ISharpenModule, ISharpenPagedResponse, ISharpenTrack } from '@shared/models/sharpen-data-model';
import { Observable, of, Subject } from 'rxjs';
import { catchError, filter, switchMap, takeUntil, tap } from 'rxjs/operators';

interface IContentState {
  pageNumber: number;
  pagesCount: number;
  content: ISharpenTrack[];
  modules: {
    [key: number]: ISharpenModule[];
  }
}

@Component({
  selector: 'app-sharpen-edit-dialog',
  templateUrl: './sharpen-edit-dialog.component.html',
  styleUrls: ['./sharpen-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharpenEditDialogComponent extends ComponentStore<IContentState> implements OnInit, OnDestroy {

  @Input() openSharpenModal: EventEmitter<boolean>;
  @Output() selectedModule: EventEmitter<number> = new EventEmitter();

  public content$: Observable<ISharpenPagedResponse<ISharpenTrack>>;
  public modules$: Observable<ISharpenPagedResponse<ISharpenModule>>;
  
  public openPreviewModal: EventEmitter<ISharpenModule | undefined> = new EventEmitter();
  public previewModule: ISharpenModule | undefined;

  private unsub$: Subject<void> = new Subject();

  private loadPage$: Subject<void> = new Subject();
  private loadModules$: Subject<{trackId: number, modulesIds: number[]}> = new Subject();
  
  constructor(
    private sharpen: SharpenService,
    private cdr: ChangeDetectorRef,
  ) { 
    super({
      pageNumber: 0,
      pagesCount: 0,
      content: [],
      modules: {},
    })
    
    this.content$ = this.loadPage$.pipe(
      switchMap(() => this.sharpen.getAllTracks(this.getState().pageNumber + 1).pipe(
        catchError((err) => {
          console.error(err);
          return of(undefined);
        }),
      ))
    );

    this.modules$ = this.loadModules$.pipe(
      switchMap((data: {trackId: number, modulesIds: number[]}) => this.sharpen.getModulesByIds(data.modulesIds).pipe(
        catchError((err) => {
          console.error(err);
          return of(undefined);
        }),
        tap((response: ISharpenPagedResponse<ISharpenModule>) => {
          if (!response) return;
            this.patchState(state => ({
              ...state,
              modules: {
                ...state.modules,
                [data.trackId]: [...response.results],
              }
            }));
        })
      ))
    );
  }

  get content(): ISharpenTrack[] {
    return this.getState().content;
  }

  getModules(trackId: number): ISharpenModule[] | undefined {
    return this.getState().modules[trackId];
  }

  public ngOnInit(): void {
    this.content$.pipe(takeUntil(this.unsub$)).subscribe((response: ISharpenPagedResponse<ISharpenTrack>) => {
      if (!response) return;
      this.patchState(state => ({
        ...state,
        pageNumber: state.pageNumber + 1,
        pagesCount: response.count,
        content: [
          ...state.content, ...response.results
        ],
      }));
      this.cdr.markForCheck();
    });

    this.modules$.pipe(takeUntil(this.unsub$)).subscribe((response: ISharpenPagedResponse<ISharpenModule>) => {
      this.cdr.markForCheck();
    });

    this.openPreviewModal.asObservable().pipe(takeUntil(this.unsub$)).subscribe((module) => this.previewModule = module);
    this.openSharpenModal?.asObservable()
      .pipe(
        takeUntil(this.unsub$),
        filter((isOpen) => !!isOpen),
      )
      .subscribe(() => this.reloadIfEmpty());

    this.loadPage$.next();
  }

  public ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  public addContent(id: number) {
    this.selectedModule.next(id);
    this.openPreviewModal.next(undefined);
    this.openSharpenModal.next(false);
  }

  public onScroll() {
    // fetch next page
    const { pageNumber, pagesCount } = this.getState();
    if (pagesCount > pageNumber) {
      this.loadPage$.next();
    }
  }

  public onExpandTrack(isExpanded: boolean, trackId: number) {
    if (!isExpanded) return;
    const { content, modules } = this.getState();
    if (!modules[trackId]) {
      const track = content?.find((item) => item.id === trackId);
      if (!track?.modules?.length) return;
      this.loadModules$.next({trackId, modulesIds: track.modules});
    }
  }

  private reloadIfEmpty(): void {
    if (this.content.length) {
      return;
    }
    this.loadPage$.next();
  }

  private getState(): IContentState {
    return this.get();
  }
}